import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IWorkshopModuleScheme } from '../types/WorkshopModuleScheme';
import { IBaseEditor } from '@start-set/wakadoo-editor-core/impl/IBaseEditor';

const initialState: IWorkshopModuleScheme = {
	editor: null,

	moduleSearch: undefined,
	tags: undefined,
	categories: undefined,
	isModulesModalActive: false,
	moduleWindowFunc: null,
	onCloseModuleModal: null,

	deletingModuleId: undefined,
	isDeletingModuleModalActive: false,

	bindingModuleId: undefined,
	isBindingTagModuleModalActive: false,
	isBindingCategoryModuleModalActive: false,

	isEditModuleModalActive: false,

	publishingModuleId: undefined,
	isPublishingModuleModalActive: false,

	isTagsDropdownOpen: false,
};

export const workshopModuleSlice = createSlice({
	name: 'workshopModule',
	initialState,
	selectors: {
		getIsEditModuleModalActive: (state) => state.isEditModuleModalActive || false,
		getIsModulesModalActive: (state) => state.isModulesModalActive || false,
		getDeletingModuleId: (state) => state.deletingModuleId || undefined,
		getIsDeletingModuleModalActive: (state) => state.isDeletingModuleModalActive || false,
		getTags: (state) => state.tags || [],
		getCategories: (state) => state.categories || [],
		getModuleSearch: (state) => state.moduleSearch || '',
		getBindingModuleId: (state) => state.bindingModuleId || undefined,
		getIsBindingTagModuleModalActive: (state) => state.isBindingTagModuleModalActive || false,
		getIsBindingCategoryModuleModalActive: (state) => state.isBindingCategoryModuleModalActive || false,

		getPublishingModuleId: (state) => state.publishingModuleId || undefined,
		getIsPublishingModuleModalActive: (state) => state.isPublishingModuleModalActive || false,

		getOnModuleClick: (state) => state.moduleWindowFunc || null,

		getIsTagsDropdownOpen: (state) => state.isTagsDropdownOpen || false,
		getEditor: (state) => state.editor,
	},
	reducers: {
		setModuleSearch(state, action: PayloadAction<string>) {
			state.moduleSearch = action.payload;
		},
		setTags(state, action: PayloadAction<number[]>) {
			state.tags = action.payload;
		},
		setCategories(state, action: PayloadAction<number[]>) {
			state.categories = action.payload;
		},
		setIsModulesModalActive(
			state,
			action: PayloadAction<{ editor?: IBaseEditor | null; active: boolean }>
		) {
			const { editor = null, active } = action.payload;
			if (active) {
				editor?.disableInteractive();
			} else {
				state.editor?.enableInteractive();
			}
			state.isModulesModalActive = active;
			state.editor = editor;
		},
		setModuleFunc: (state, action: PayloadAction<(moduleId: string) => void>) => {
			state.moduleWindowFunc = action.payload;
		},
		setCloseModuleModalFunc: (state, action: PayloadAction<() => void>) => {
			state.onCloseModuleModal = action.payload;
		},

		setDeletingModuleId(state, action: PayloadAction<string | undefined>) {
			state.deletingModuleId = action.payload;
		},
		setIsDeletingModuleModalActive(state, action: PayloadAction<boolean>) {
			state.isDeletingModuleModalActive = action.payload;
		},

		setBindingModuleId(state, action: PayloadAction<string | undefined>) {
			state.bindingModuleId = action.payload;
		},
		setIsBindingTagModuleModalActive(state, action: PayloadAction<boolean>) {
			state.isBindingTagModuleModalActive = action.payload;
		},
		setIsBindingCategoryModuleModalActive(state, action: PayloadAction<boolean>) {
			state.isBindingCategoryModuleModalActive = action.payload;
		},
		setIsEditModuleModalActive(state, action: PayloadAction<boolean>) {
			state.isEditModuleModalActive = action.payload;
		},

		setPublishingModuleId(state, action: PayloadAction<string | undefined>) {
			state.publishingModuleId = action.payload;
		},
		setIsPublishingModuleModalActive(state, action: PayloadAction<boolean>) {
			state.isPublishingModuleModalActive = action.payload;
		},
		setIsTagsDropdownOpen: (state, action: PayloadAction<boolean>) => {
			state.isTagsDropdownOpen = action.payload;
		},
	},
});

export const {
	actions: workshopModuleActions,
	reducer: workshopModuleReducer,
	selectors: workshopModuleSelectors,
} = workshopModuleSlice;
